import { inject, Injectable } from '@angular/core';
import { Observable, Subject, catchError, map, throwError } from 'rxjs';

import { LoggingService } from '../logging/logging.service';

export interface SSEMessage<T = unknown> {
  data: T;
  event?: string;
  id?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SSEService {
  private readonly loggingService = inject(LoggingService);
  private baseUrl = '';

  private connections = new Map<string, { eventSource: EventSource; subject: Subject<SSEMessage> }>();

  /**
   * Setter for the base URL used in the service for all SSE connections.
   *
   * @param baseUrl The base URL to set.
   */
  public setBaseUrl(baseUrl: string): void {
    this.baseUrl = baseUrl;
  }

  /**
   * Watch SSE messages from a specific topic endpoint.
   *
   * @param topic The topic/endpoint path to watch messages from.
   * @param connectionId Optional connection ID for managing multiple connections.
   *
   * @returns Observable for watching SSE messages.
   *
   * @throws Error, if SSE service is not initialized (baseUrl not set).
   */
  public watchTopic<T>(topic: string, connectionId: string = topic): Observable<SSEMessage<T>> {
    if (!this.baseUrl) {
      throw new Error('Unable to watch topic: SSE service is not initialized. Please call "setBaseUrl()" first.');
    }

    // Close existing connection if any
    this.disconnect(connectionId);

    let url = `${this.baseUrl}${topic}`;

    if (url.includes('?')) {
      url = `${url}&ngsw-bypass=true`;
    } else {
      url = `${url}?ngsw-bypass=true`;
    }

    const eventSource = new EventSource(url, { withCredentials: true });
    const subject = new Subject<SSEMessage>();

    // Store the connection
    this.connections.set(connectionId, { eventSource, subject });

    // Handle SSE open
    eventSource.onopen = (): void => {
      this.loggingService.debug(`SSE connection opened for ${connectionId}`);
    };

    // Handle SSE messages
    eventSource.onmessage = (event): void => {
      try {
        const data = JSON.parse(event.data);

        subject.next({
          data,
          event: event.type,
          id: event.lastEventId
        });
      } catch (error) {
        this.loggingService.error('Error parsing SSE message:', error);
        subject.error(error);
      }
    };

    // Handle SSE errors
    eventSource.onerror = (): void => {
      if (eventSource.readyState === EventSource.CLOSED) {
        this.loggingService.debug(`SSE connection closed for ${connectionId}`);
        subject.complete();
        this.connections.delete(connectionId);
      } else {
        this.loggingService.error(`SSE connection error for ${connectionId}`);
        subject.error(new Error('SSE connection error'));
      }
    };

    return subject.asObservable().pipe(
      map(message => message as SSEMessage<T>),
      catchError(error => {
        this.loggingService.error('SSE subscription failed:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Disconnect from an SSE endpoint.
   *
   * @param connectionId The connection ID to disconnect.
   */
  public disconnect(connectionId: string): void {
    const connection = this.connections.get(connectionId);

    if (connection) {
      connection.eventSource.close();
      connection.subject.complete();
      this.connections.delete(connectionId);
      this.loggingService.debug(`Disconnected from SSE ${connectionId}`);
    }
  }

  /**
   * Disconnect from all SSE connections.
   */
  public disconnectAll(): void {
    this.connections.forEach((_, connectionId) => {
      this.disconnect(connectionId);
    });
  }

  /**
   * Check if a connection exists.
   *
   * @param connectionId The connection ID to check.
   *
   * @returns True if the connection exists, false otherwise.
   */
  public isConnected(connectionId: string): boolean {
    return this.connections.has(connectionId);
  }
}
